import {FC, useCallback} from "react";
import styled from "@emotion/styled/macro";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {setIsOpenPlayerPool} from "modules/actions";
import {IPlayer} from "modules/types";
import {
	getDashedValue,
	getPlayerAvatarSrc,
	getPlayerName,
	getPlayerStats,
	getStatsValue,
	toLowerCase,
} from "modules/utils";
import {getSquadById} from "modules/selectors";
import {breakpoints} from "assets/theme";

const Title = styled.div`
	text-align: center;
	font-size: calc(24px + 1vw);
	font-weight: 700;
	line-height: 120%;
	letter-spacing: 0.2px;
	text-transform: uppercase;
	margin-bottom: 8px;

	@media (min-width: 1440px) {
		font-size: 60px;
	}
`;

const Desc = styled.div`
	font-family: ${({theme}) => theme.secondFont};
	font-size: 14px;
	font-weight: 500;
	line-height: 140%;
	letter-spacing: 0.56px;
	margin-bottom: 8px;

	@media (min-width: ${breakpoints.mobile}) {
		font-size: 20px;
	}

	@media (min-width: ${breakpoints.tablet}) {
		font-size: 28px;
	}
`;

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	padding: 20px 20px 32px;
	color: ${({theme}) => theme.colorPalette.white};

	@media (min-width: ${breakpoints.tablet}) {
		padding: 20px 66px 45px;
		min-height: 737px;
	}
`;

const ImgWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	position: relative;
	margin-bottom: 24px;
	max-width: 282px;

	img {
		width: 100%;
	}

	@media (min-width: ${breakpoints.mobile}) {
		width: 45%;
	}

	@media (min-width: ${breakpoints.tablet}) {
		width: 70%;
	}

	&:hover svg circle {
		fill: #a408a4;
	}
`;

const Stats = styled.div`
	width: 100%;
	max-width: 460px;
	display: flex;
	justify-content: center;
	flex-wrap: wrap;

	& > * {
		box-sizing: content-box;
		width: calc(33.3% - 10px); // 1/3 - border width - padding width

		&:nth-child(n + 4):nth-child(-n + 6) {
			background: #848484;
			border-top: none;
		}

		&:not(:nth-child(3n)) {
			border-right: none;
		}
	}
`;

const StatItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	align-items: center;
	justify-content: center;
	padding: 8px 4px;
	color: ${({theme}) => theme.colorPalette.white};
	background: ${({theme}) => theme.colorPalette.black};
	word-break: break-word;
	text-align: center;
	border: 1px solid #cccccc;

	&&:last-of-type {
		border-right: 1px solid #cccccc;
	}
`;

const Value = styled.div`
	font-weight: 700;
	font-size: 20px;
	line-height: 120%;
	letter-spacing: 0.2px;

	@media (min-width: ${breakpoints.medium}) {
		font-size: 24px;
	}
`;

const Name = styled.div`
	font-family: "Hind", sans-serif;
	font-weight: 400;
	font-size: 12px;
	line-height: 120%;
	letter-spacing: 0.02em;
`;

interface IFanVotePlayer {
	player: IPlayer;
}

export const FanVotePlayer: FC<IFanVotePlayer> = ({player}) => {
	const {t} = useTranslation();
	const dispatch = useDispatch();
	const squad = useSelector(getSquadById)(player.squadId);
	const stats = getPlayerStats(player);

	const clickHandler = useCallback(() => {
		dispatch(setIsOpenPlayerPool(true));
	}, [dispatch]);

	return (
		<Wrapper>
			<ImgWrapper onClick={clickHandler}>
				<img src={getPlayerAvatarSrc(player)} alt={getPlayerName(player)} />
			</ImgWrapper>

			<Title>{getPlayerName(player)}</Title>
			<Desc>
				{getDashedValue(squad?.name)}
				<span> | </span>
				{t(toLowerCase(`page.vote.player.${player.position}`))}
			</Desc>

			<Stats>
				{stats.map(({value, key, isShowDecimal}) => (
					<StatItem key={key}>
						<Value>{getStatsValue(value, isShowDecimal)}</Value>
						<Name>{t(key)}</Name>
					</StatItem>
				))}
			</Stats>
		</Wrapper>
	);
};
